/* eslint-disable react/no-unknown-property -- react-three/fiber uses JSX props ESLint doesn't recognise */
/**
 * Geological Modelling tab — Phase E (Iter 28).
 *
 * Sub-tabs:
 *   1. Ore shell   — build an implicit iso-surface from drillhole assays.
 *   2. Explicit    — Delaunay surface from a picked-points textarea.
 *   3. Implicit    — RBF iso-surface from a scattered scalar dataset.
 *   4. Faults      — apply a fault set to a stored mesh + get counts.
 *   5. Validate    — topology + repair report for the currently loaded mesh.
 *
 * Meshes are rendered in a small three-fiber canvas. Nothing is persisted;
 * everything is a compute-and-preview flow that the geologist can export
 * downstream via the reports pipeline.
 */
import React, { useMemo, useRef, useState, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  Layers, Mountain, Sparkles, GitBranchPlus, ShieldCheck, Loader2, Play,
  Info, AlertTriangle, Gauge, Boxes,
} from "lucide-react";
import api from "@/lib/api";
import FullscreenPanel from "@/components/FullscreenPanel";
import StageModeSwitcher, { useStageMode } from "@/components/StageModeSwitcher";


export default function GeoModellingTab({ projectId }) {
  const [mesh, setMesh] = useState(null);
  const [topology, setTopology] = useState(null);
  const setResult = (m, t) => { setMesh(m); setTopology(t); };

  return (
    <section className="p-4 space-y-4" data-testid="geo-modelling-tab">
      <header className="border-b border-border pb-3">
        <div className="smin-label">Geological Modelling</div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">
          Ore shells · Explicit · Implicit · Faults · Validate
        </h2>
        <p className="text-xs text-muted-foreground mt-1 font-mono">
          Build wireframe surfaces from picked points or drillhole assays,
          apply fault networks and audit topology in one workspace.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4">
        <Tabs defaultValue="oreshell" className="w-full">
          <TabsList className="rounded-none border-b border-border bg-transparent px-0 gap-2 h-11 justify-start">
            <TabsTrigger value="oreshell" data-testid="geo-tab-oreshell" className="rounded-none">
              <Mountain className="h-3.5 w-3.5 mr-1.5" /> Ore shell
            </TabsTrigger>
            <TabsTrigger value="explicit" data-testid="geo-tab-explicit" className="rounded-none">
              <Layers className="h-3.5 w-3.5 mr-1.5" /> Explicit
            </TabsTrigger>
            <TabsTrigger value="implicit" data-testid="geo-tab-implicit" className="rounded-none">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Implicit
            </TabsTrigger>
            <TabsTrigger value="faults" data-testid="geo-tab-faults" className="rounded-none">
              <GitBranchPlus className="h-3.5 w-3.5 mr-1.5" /> Faults
            </TabsTrigger>
            <TabsTrigger value="validate" data-testid="geo-tab-validate" className="rounded-none">
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Validate
            </TabsTrigger>
          </TabsList>

          <TabsContent value="oreshell" className="pt-4">
            <OreShellPanel projectId={projectId} onResult={setResult} />
          </TabsContent>
          <TabsContent value="explicit" className="pt-4">
            <ExplicitPanel projectId={projectId} onResult={setResult} />
          </TabsContent>
          <TabsContent value="implicit" className="pt-4">
            <ImplicitPanel projectId={projectId} onResult={setResult} />
          </TabsContent>
          <TabsContent value="faults" className="pt-4">
            <FaultsPanel projectId={projectId} mesh={mesh} />
          </TabsContent>
          <TabsContent value="validate" className="pt-4">
            <ValidatePanel projectId={projectId} mesh={mesh} onResult={setResult} />
          </TabsContent>
        </Tabs>

        <MeshPreview mesh={mesh} topology={topology} />
      </div>
    </section>
  );
}


/* ============================================================
 * Mesh preview — three-fiber canvas + topology badge
 * ============================================================ */
function MeshPreview({ mesh, topology }) {
  return (
    <FullscreenPanel title={`3D preview${mesh ? ` · ${mesh.name || "mesh"} · ${mesh.triangles?.length || 0} tri` : ""}`}
                     testid="geo-mesh-fullscreen">
    <div className="border border-border bg-card p-3 relative min-h-[440px] h-full flex flex-col"
         data-testid="geo-mesh-preview">
      <div className="smin-label flex items-center justify-between pr-10">
        <span>3D preview {mesh ? `· ${mesh.name || "mesh"} · ${mesh.triangles?.length || 0} tri` : ""}</span>
        {topology && (
          <span className="font-mono text-[10px] uppercase tracking-widest">
            {topology.watertight
              ? <span className="text-primary">◉ watertight</span>
              : <span className="text-amber-600">◍ open</span>}
            {" · "}
            {topology.orientable
              ? <span className="text-emerald-600">orientable</span>
              : <span className="text-red-600">non-orientable</span>}
            {topology.genus != null && <> · g={topology.genus}</>}
          </span>
        )}
      </div>
      <div className="flex-1 mt-2 border border-border/60 relative overflow-hidden">
        {mesh ? (
          <Canvas
            camera={{ position: [200, 200, 200], fov: 45 }}
            style={{ background: "#F8FAFC" }}
            data-testid="geo-canvas"
          >
            <ambientLight intensity={0.6} />
            <directionalLight position={[100, 200, 100]} intensity={0.8} />
            <MeshFromArrays vertices={mesh.vertices} triangles={mesh.triangles} />
            <axesHelper args={[80]} />
            <OrbitControls makeDefault />
          </Canvas>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-muted-foreground text-xs font-mono italic">
            Build a surface on the left to preview it here.
          </div>
        )}
      </div>
      {topology && (
        <div className="mt-2 grid grid-cols-4 gap-1 text-[10px] font-mono">
          <Chip label="verts"  value={mesh?.vertices?.length ?? 0} />
          <Chip label="tri"    value={mesh?.triangles?.length ?? 0} />
          <Chip label="bnd"    value={topology.boundary_edges} />
          <Chip label="nm"     value={topology.non_manifold_edges} />
        </div>
      )}
    </div>
    </FullscreenPanel>
  );
}

function MeshFromArrays({ vertices, triangles }) {
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const verts = new Float32Array(vertices.flat());
    const tris = new Uint32Array(triangles.flat());
    g.setAttribute("position", new THREE.BufferAttribute(verts, 3));
    g.setIndex(new THREE.BufferAttribute(tris, 1));
    g.computeVertexNormals();
    // Centre + scale to fit ~200-unit box
    g.computeBoundingBox();
    const box = g.boundingBox;
    if (box) {
      const cx = (box.min.x + box.max.x) / 2;
      const cy = (box.min.y + box.max.y) / 2;
      const cz = (box.min.z + box.max.z) / 2;
      g.translate(-cx, -cy, -cz);
    }
    return g;
  }, [vertices, triangles]);
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial color="#FF5722" metalness={0.15} roughness={0.5} side={THREE.DoubleSide} />
    </mesh>
  );
}

function Chip({ label, value }) {
  return (
    <div className="border border-border/70 px-2 py-1 flex justify-between">
      <span className="text-muted-foreground uppercase tracking-widest text-[9px]">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}


/* ============================================================
 * Ore-shell — Sprint A (Feb 2026)
 *
 * Leapfrog-style UX: two intuitive sliders (Smoothness / Tolerance) drive
 * the RBF ε and the iso-shift on the backend. Every parameter change is
 * debounced (600 ms) and triggers a live rebuild that renders the shell
 * PLUS a metrics readout — Volume, Tonnage, Avg grade, Contained metal.
 * ============================================================ */
function OreShellPanel({ projectId, onResult }) {
  const [mode, setMode] = useState("ai");   // "ai" | "manual"
  const [aiStats, setAiStats] = useState(null);
  const [cutoff, setCutoff] = useState(0.5);
  const [nx, setNx] = useState(24);
  const [padding, setPadding] = useState(30);
  const [smoothness, setSmoothness] = useState(50);   // 0..100
  const [tolerance,  setTolerance]  = useState(50);   // 0..100
  const [density,    setDensity]    = useState(2.7);   // t/m³
  const [busy, setBusy] = useState(false);
  const [aiReason, setAiReason] = useState("");
  const [metrics, setMetrics] = useState(null);        // last live-preview payload
  const [autoPreview, setAutoPreview] = useState(true);

  // Pull the real assay stats so the cutoff default reflects THIS project,
  // not an arbitrary literal that may sit outside the field range.
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/ai-advisor/assay-stats`)
      .then(({ data }) => {
        setAiStats(data);
        const g = data?.grade || {};
        if (mode === "ai" && g.count) {
          const suggested = g.p60 ?? g.median ?? g.mean;
          if (suggested != null) {
            setCutoff(Number(Number(suggested).toFixed(3)));
            setAiReason(`p60=${(g.p60 ?? 0).toFixed(3)} · range [${(g.min ?? 0).toFixed(3)}, ${(g.max ?? 0).toFixed(3)}] · CV ${(g.cv ?? 0).toFixed(2)}`);
          }
        }
      })
      .catch(() => setAiStats(null));
  }, [projectId]);

  useEffect(() => {
    // If the user flips back to AI mode, re-apply the recommended value
    if (mode !== "ai" || !aiStats) return;
    const g = aiStats.grade || {};
    const suggested = g.p60 ?? g.median ?? g.mean;
    if (suggested != null) setCutoff(Number(Number(suggested).toFixed(3)));
  }, [mode, aiStats]);

  const outOfRange = aiStats?.grade?.count && (
    cutoff < aiStats.grade.min || cutoff > aiStats.grade.max
  );

  const run = React.useCallback(async ({ silent = false } = {}) => {
    if (outOfRange) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/geomodel/ore-shell`, {
        cutoff_grade: parseFloat(cutoff),
        grid_shape: [+nx, +nx, Math.max(8, Math.floor(nx * 0.6))],
        padding: parseFloat(padding),
        smoothness: parseFloat(smoothness),
        tolerance:  parseFloat(tolerance),
        density:    parseFloat(density),
      });
      onResult(data.mesh, data.topology);
      setMetrics(data);
      if (!silent) toast.success(`Ore shell built · ${data.mesh.triangles.length} tri`);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Ore shell failed");
      setMetrics(null);
    }
    finally { setBusy(false); }
  }, [projectId, cutoff, nx, padding, smoothness, tolerance, density, outOfRange, onResult]);

  // Debounced live preview — rebuild 600 ms after the last slider tweak.
  const _debTimer = React.useRef(null);
  useEffect(() => {
    if (!autoPreview || !projectId) return;
    if (outOfRange) return;
    if (_debTimer.current) clearTimeout(_debTimer.current);
    _debTimer.current = setTimeout(() => run({ silent: true }), 600);
    return () => _debTimer.current && clearTimeout(_debTimer.current);
  }, [smoothness, tolerance, cutoff, nx, padding, density, autoPreview, projectId, outOfRange, run]);

  return (
    <div className="border border-border bg-card p-4 space-y-3" data-testid="oreshell-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground font-mono flex-1">
          Builds an implicit iso-surface directly from the project&apos;s drillhole
          assays. Each assay midpoint becomes an RBF centre weighted by grade.
        </p>
        <StageModeSwitcher
          mode={mode}
          onChange={setMode}
          hideModes={["import"]}
          testidPrefix="oreshell-mode"
        />
      </div>
      {aiStats?.grade?.count > 0 && (
        <div className="flex items-center gap-2 text-[11px] font-mono text-neutral-700 flex-wrap"
             data-testid="oreshell-stats-hint">
          <Info className="h-3.5 w-3.5 text-[#4CAF50]" />
          Assay grade&nbsp;<span className="text-neutral-900">[{aiStats.grade.min.toFixed(3)}, {aiStats.grade.max.toFixed(3)}]</span>&nbsp;·
          median&nbsp;<span className="text-neutral-900">{(aiStats.grade.median || 0).toFixed(3)}</span>&nbsp;·
          p60&nbsp;<span className="text-neutral-900">{(aiStats.grade.p60 || 0).toFixed(3)}</span>&nbsp;·
          {aiStats.assay_count} assays
          {mode === "ai" && aiReason && (
            <span className="ml-2 inline-flex items-center gap-1 text-[#4CAF50]"
                  title={aiReason}>
              <Sparkles className="h-3 w-3" /> AI set cutoff = p60
            </span>
          )}
        </div>
      )}

      {/* --- Row 1 · Cutoff / Grid / Padding / Density -------------------- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-xs">
        <FormRow label="Cutoff grade">
          <Input
            type="number" step="0.05" value={cutoff}
            data-testid="oreshell-cutoff"
            onChange={(e) => { setMode("manual"); setCutoff(e.target.value); }}
            className={`rounded-none bg-input ${outOfRange ? "border-red-500" : ""}`}
          />
        </FormRow>
        <FormRow label="Grid density (nx=ny)">
          <Input type="number" step="1" value={nx}
                 data-testid="oreshell-grid"
                 onChange={(e) => setNx(e.target.value)}
                 className="rounded-none bg-input" />
        </FormRow>
        <FormRow label="Padding (m)">
          <Input type="number" step="1" value={padding}
                 data-testid="oreshell-padding"
                 onChange={(e) => setPadding(e.target.value)}
                 className="rounded-none bg-input" />
        </FormRow>
        <FormRow label="Bulk density (t/m³)">
          <Input type="number" step="0.05" value={density}
                 data-testid="oreshell-density"
                 onChange={(e) => setDensity(e.target.value)}
                 className="rounded-none bg-input" />
        </FormRow>
      </div>

      {/* --- Row 2 · Smoothness & Tolerance sliders ----------------------- */}
      <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-border">
        <div className="space-y-2" data-testid="oreshell-smoothness-block">
          <div className="flex items-center justify-between font-mono text-[11px]">
            <span className="flex items-center gap-1.5 text-neutral-700">
              <Gauge className="h-3.5 w-3.5 text-[#4CAF50]" /> Smoothness
            </span>
            <span className="text-neutral-900 tabular-nums">{smoothness}</span>
          </div>
          <Slider
            value={[smoothness]}
            min={0} max={100} step={1}
            onValueChange={(v) => setSmoothness(v[0])}
            data-testid="oreshell-smoothness-slider"
          />
          <p className="text-[10px] text-neutral-500 leading-tight">
            Tight kernel (low) hugs each assay hotspot; broad kernel (high) blends
            neighbours into one smooth continuous shell.
          </p>
        </div>
        <div className="space-y-2" data-testid="oreshell-tolerance-block">
          <div className="flex items-center justify-between font-mono text-[11px]">
            <span className="flex items-center gap-1.5 text-neutral-700">
              <Boxes className="h-3.5 w-3.5 text-[#4CAF50]" /> Grade Tolerance
            </span>
            <span className="text-neutral-900 tabular-nums">{tolerance}</span>
          </div>
          <Slider
            value={[tolerance]}
            min={0} max={100} step={1}
            onValueChange={(v) => setTolerance(v[0])}
            data-testid="oreshell-tolerance-slider"
          />
          <p className="text-[10px] text-neutral-500 leading-tight">
            Low tolerance = tight hug around high-grade centres only (smaller
            shell). High tolerance = looser boundary, larger shell.
          </p>
        </div>
      </div>

      {outOfRange && (
        <div className="flex items-start gap-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2"
             data-testid="oreshell-oor-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            cutoff={cutoff} is outside your assay range
            [{aiStats.grade.min.toFixed(3)}, {aiStats.grade.max.toFixed(3)}]. Try{" "}
            <button type="button" className="underline"
                    onClick={() => { setMode("ai"); setCutoff(Number((aiStats.grade.p60 ?? aiStats.grade.median).toFixed(3))); }}>
              use AI-recommended p60 ({(aiStats.grade.p60 ?? aiStats.grade.median).toFixed(3)})
            </button>.
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={() => run()} disabled={busy || outOfRange} data-testid="oreshell-run"
                className="rounded-none font-heading">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          Build ore shell
        </Button>
        <label className="flex items-center gap-2 text-[11px] font-mono text-neutral-700 select-none cursor-pointer">
          <input
            type="checkbox"
            checked={autoPreview}
            onChange={(e) => setAutoPreview(e.target.checked)}
            data-testid="oreshell-autopreview"
            className="h-3.5 w-3.5"
          />
          Auto-preview on slider change
        </label>
      </div>

      {/* --- Live metrics readout ---------------------------------------- */}
      {metrics && (
        <OreShellMetricsReadout metrics={metrics} density={density} />
      )}
    </div>
  );
}

/**
 * OreShellMetricsReadout — 4-cell metrics panel rendered under the sliders
 * whenever the last live preview succeeded. Values are formatted with the
 * SsoT tonnage rules (`t` if < 1 Mt, else `Mt`, always 2 decimals).
 */
function OreShellMetricsReadout({ metrics, density }) {
  const fmtT = (v) => {
    const n = Number(v || 0);
    if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mt`;
    return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t`;
  };
  const fmtV = (v) => {
    const n = Number(v || 0);
    if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mm³`;
    return `${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} m³`;
  };
  const cells = [
    { label: "Shell volume",     value: fmtV(metrics.volume_m3), sub: `density ${density} t/m³` },
    { label: "Approx. tonnage",  value: fmtT(metrics.tonnage),   sub: "volume × density" },
    { label: "Avg grade (inside)", value: (metrics.avg_grade || 0).toFixed(4), sub: `${metrics.samples_inside}/${metrics.samples_total} assays` },
    { label: "Contained metal",  value: fmtT(metrics.contained_metal_t), sub: "tonnage × avg grade" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-3 border-t border-border"
         data-testid="oreshell-metrics">
      {cells.map((c) => (
        <div key={c.label}
             className="border border-neutral-200 bg-white p-2.5 flex flex-col gap-0.5"
             data-testid={`oreshell-metric-${c.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-mono">
            {c.label}
          </div>
          <div className="text-lg font-heading font-black text-neutral-900 tabular-nums">
            {c.value}
          </div>
          <div className="text-[10px] text-neutral-400 font-mono">{c.sub}</div>
        </div>
      ))}
      <div className="col-span-2 md:col-span-4 flex items-center justify-between text-[10px] font-mono text-neutral-500 border-t border-dashed border-neutral-200 pt-2 mt-1">
        <span>ε = {metrics.epsilon?.toFixed(1)} m · iso = {metrics.iso_value?.toFixed(4)}</span>
        <span>{metrics.mesh?.triangles?.length?.toLocaleString?.() || 0} triangles</span>
      </div>
    </div>
  );
}


/* ============================================================
 * Explicit
 * ============================================================ */
function ExplicitPanel({ projectId, onResult }) {
  const [txt, setTxt] = useState(
    "0 0 100\n50 0 105\n100 0 100\n0 50 108\n50 50 115\n100 50 110\n0 100 100\n50 100 105\n100 100 100",
  );
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const pts = txt.trim().split(/\n+/).map((line) => line.trim().split(/[\s,]+/).map(Number)).filter((r) => r.length === 3);
    if (pts.length < 3) { toast.error("Need at least 3 XYZ points"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/geomodel/explicit`, {
        points: pts, name: "explicit_topo",
      });
      onResult(data.mesh, data.topology);
      toast.success(`Explicit surface · ${data.mesh.triangles.length} tri`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Explicit build failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border bg-card p-4 space-y-3" data-testid="explicit-panel">
      <p className="text-[11px] text-muted-foreground font-mono">
        Paste picked control points as one &quot;x y z&quot; per line. A Delaunay
        tessellation over the (x, y) projection produces the surface.
      </p>
      <textarea
        value={txt}
        onChange={(e) => setTxt(e.target.value)}
        data-testid="explicit-points"
        rows={9}
        className="w-full bg-input border border-border rounded-none px-2 py-2 font-mono text-xs"
      />
      <Button onClick={run} disabled={busy} data-testid="explicit-run"
              className="rounded-none font-heading">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
        Build explicit surface
      </Button>
    </div>
  );
}


/* ============================================================
 * Implicit
 * ============================================================ */
function ImplicitPanel({ projectId, onResult }) {
  const [txt, setTxt] = useState(
    // (x y z value) per line — a demo dataset shaped like a 3-D blob
    "0 0 0 1.0\n50 0 0 0.9\n0 50 0 0.85\n0 0 50 0.7\n50 50 0 0.95\n50 0 50 0.8\n0 50 50 0.75\n50 50 50 0.9\n25 25 25 1.2",
  );
  const [iso, setIso] = useState(0.6);
  const [mode, setMode] = useState("ai");  // "ai" | "manual"
  const [busy, setBusy] = useState(false);

  // Derive the actual sample-value range from whatever the user has pasted
  // so the iso default is guaranteed to sit inside the field range —
  // fixes the "iso_value=30 outside [0.533, 0.767]" class of bugs.
  const dataStats = useMemo(() => {
    const vals = txt.trim().split(/\n+/)
      .map((line) => line.trim().split(/[\s,]+/).map(Number))
      .filter((r) => r.length === 4 && r.every((v) => Number.isFinite(v)))
      .map((r) => r[3]);
    if (!vals.length) return null;
    const sorted = [...vals].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    return { min: sorted[0], max: sorted[sorted.length - 1], median, n: sorted.length };
  }, [txt]);

  // In AI / auto mode, keep the iso in sync with the data median.
  useEffect(() => {
    if (mode !== "ai" || !dataStats) return;
    setIso(Number(dataStats.median.toFixed(3)));
  }, [mode, dataStats]);

  const outOfRange = dataStats && (iso < dataStats.min || iso > dataStats.max);

  const run = async () => {
    const rows = txt.trim().split(/\n+/).map((line) => line.trim().split(/[\s,]+/).map(Number)).filter((r) => r.length === 4);
    if (rows.length < 4) { toast.error("Need >= 4 (x y z value) rows"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/geomodel/implicit`, {
        samples_xyz: rows.map((r) => r.slice(0, 3)),
        samples_val: rows.map((r) => r[3]),
        iso_value: parseFloat(iso),
        name: "implicit_iso",
      });
      onResult(data.mesh, data.topology);
      toast.success(`Iso surface · ${data.mesh.triangles.length} tri`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Implicit build failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border bg-card p-4 space-y-3" data-testid="implicit-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground font-mono flex-1">
          Iso-surface via RBF interpolation + marching cubes. Paste
          (x y z value) rows below and pick an iso-value inside the range.
        </p>
        <StageModeSwitcher
          mode={mode}
          onChange={setMode}
          hideModes={["import"]}
          testidPrefix="implicit-mode"
        />
      </div>
      <textarea
        value={txt}
        onChange={(e) => setTxt(e.target.value)}
        data-testid="implicit-samples"
        rows={7}
        className="w-full bg-input border border-border rounded-none px-2 py-2 font-mono text-xs"
      />
      {dataStats && (
        <div className="flex items-center gap-2 text-[11px] font-mono text-neutral-700"
             data-testid="implicit-range-hint">
          <Info className="h-3.5 w-3.5 text-[#4CAF50]" />
          Data range&nbsp;<span className="text-neutral-900">[{dataStats.min.toFixed(3)}, {dataStats.max.toFixed(3)}]</span>&nbsp;·
          median&nbsp;<span className="text-neutral-900">{dataStats.median.toFixed(3)}</span>
          {mode === "ai" && (
            <span className="ml-2 inline-flex items-center gap-1 text-[#4CAF50]">
              <Sparkles className="h-3 w-3" /> AI auto-set iso = median
            </span>
          )}
        </div>
      )}
      <FormRow label="Iso value">
        <Input
          type="number" step="0.05" value={iso}
          data-testid="implicit-iso"
          onChange={(e) => { setMode("manual"); setIso(e.target.value); }}
          className={`rounded-none bg-input ${outOfRange ? "border-red-500" : ""}`}
        />
      </FormRow>
      {outOfRange && (
        <div className="flex items-start gap-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2"
             data-testid="implicit-oor-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            iso_value={iso} is outside the sample range [{dataStats.min.toFixed(3)}, {dataStats.max.toFixed(3)}].
            The build will fail — try <button type="button" className="underline"
              onClick={() => { setMode("ai"); setIso(Number(dataStats.median.toFixed(3))); }}>
              use median ({dataStats.median.toFixed(3)})
            </button>.
          </span>
        </div>
      )}
      <Button onClick={run} disabled={busy || outOfRange} data-testid="implicit-run"
              className="rounded-none font-heading">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
        Build iso-surface
      </Button>
    </div>
  );
}


/* ============================================================
 * Faults
 * ============================================================ */
function FaultsPanel({ projectId, mesh }) {
  const [faults, setFaults] = useState([
    { name: "F1", origin: [50, 50, 50], normal: [1, 0, 0.5], displacement: 0 },
  ]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const updateFault = (i, k, v) => {
    const arr = [...faults];
    if (k === "origin" || k === "normal") arr[i][k] = v.split(/[\s,]+/).map(Number);
    else arr[i][k] = v;
    setFaults(arr);
  };
  const add = () => setFaults([...faults, { name: `F${faults.length + 1}`, origin: [0, 0, 0], normal: [1, 0, 0], displacement: 0 }]);

  const run = async () => {
    if (!mesh) { toast.error("Build a mesh on another tab first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/geomodel/faults/apply`, {
        faults, mesh: { vertices: mesh.vertices, triangles: mesh.triangles },
      });
      setRes(data);
      toast.success(`Faults applied · ${data.faults.length} fault(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fault apply failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-4 space-y-3" data-testid="faults-panel">
      <p className="text-[11px] text-muted-foreground font-mono">
        Define faults by a point on the plane + a normal vector. Applies
        against the surface currently loaded in the preview.
      </p>
      {faults.map((f, i) => (
        <div key={i} className="border border-border p-2 space-y-2 text-xs font-mono" data-testid={`fault-${i}`}>
          <div className="grid grid-cols-3 gap-2">
            <label className="space-y-0.5">
              <span className="text-muted-foreground text-[9px] uppercase">Name</span>
              <Input value={f.name}
                     data-testid={`fault-${i}-name`}
                     onChange={(e) => updateFault(i, "name", e.target.value)}
                     className="rounded-none bg-input h-8 text-[11px]" />
            </label>
            <label className="space-y-0.5">
              <span className="text-muted-foreground text-[9px] uppercase">Origin (x y z)</span>
              <Input value={f.origin.join(" ")}
                     data-testid={`fault-${i}-origin`}
                     onChange={(e) => updateFault(i, "origin", e.target.value)}
                     className="rounded-none bg-input h-8 text-[11px]" />
            </label>
            <label className="space-y-0.5">
              <span className="text-muted-foreground text-[9px] uppercase">Normal (x y z)</span>
              <Input value={f.normal.join(" ")}
                     data-testid={`fault-${i}-normal`}
                     onChange={(e) => updateFault(i, "normal", e.target.value)}
                     className="rounded-none bg-input h-8 text-[11px]" />
            </label>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={add}
                data-testid="faults-add"
                className="rounded-none font-mono text-[10px] h-8">+ Add fault</Button>
        <Button onClick={run} disabled={busy || !mesh}
                data-testid="faults-run"
                className="rounded-none font-heading">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          Apply to mesh
        </Button>
      </div>
      {res && (
        <div className="grid grid-cols-3 gap-2 text-[11px] font-mono border-t border-border pt-3">
          {res.faults.map((r) => (
            <div key={r.fault} className="border border-border/60 p-2">
              <div className="text-primary font-heading">{r.fault}</div>
              <div>HW: {r.hanging_wall_vertices}</div>
              <div>FW: {r.foot_wall_vertices}</div>
              <div className="text-muted-foreground">on: {r.on_plane_vertices}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ============================================================
 * Validate + Repair
 * ============================================================ */
function ValidatePanel({ projectId, mesh, onResult }) {
  const [rep, setRep] = useState(null);
  const [topo, setTopo] = useState(null);
  const [busy, setBusy] = useState(false);
  const validate = async () => {
    if (!mesh) { toast.error("Build a mesh on another tab first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/geomodel/validate`, {
        mesh: { vertices: mesh.vertices, triangles: mesh.triangles },
      });
      setRep(data);
      const t = await api.post(`/projects/${projectId}/geomodel/topology`, {
        mesh: { vertices: mesh.vertices, triangles: mesh.triangles },
      });
      setTopo(t.data);
      toast.success("Surface validated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Validation failed"); }
    finally { setBusy(false); }
  };
  const repair = async () => {
    if (!mesh) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/geomodel/repair`, {
        mesh: { vertices: mesh.vertices, triangles: mesh.triangles },
      });
      onResult(data.mesh, data.topology_after);
      toast.success(`Repaired · deduped=${data.stats.deduped}, degen=${data.stats.degenerate}, oor=${data.stats.out_of_range}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Repair failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border bg-card p-4 space-y-3" data-testid="validate-panel">
      <p className="text-[11px] text-muted-foreground font-mono">
        Runs a topological audit and offers a one-click repair (deduplicate,
        drop degenerate, drop out-of-range triangles).
      </p>
      <div className="flex gap-2">
        <Button onClick={validate} disabled={busy || !mesh}
                data-testid="validate-run"
                className="rounded-none font-heading">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
          Validate
        </Button>
        <Button variant="outline" onClick={repair} disabled={busy || !mesh}
                data-testid="repair-run"
                className="rounded-none font-heading">
          Repair
        </Button>
      </div>
      {rep && (
        <div className="grid grid-cols-2 gap-2 text-[11px] font-mono border-t border-border pt-3">
          {[
            ["Vertices",            rep.n_vertices],
            ["Triangles",           rep.n_triangles],
            ["Duplicate triangles", rep.n_duplicate_triangles],
            ["Degenerate",          rep.n_degenerate_triangles],
            ["Non-manifold edges",  rep.n_non_manifold_edges],
            ["Boundary edges",      rep.n_boundary_edges],
            ["Watertight",          rep.is_watertight ? "✓ yes" : "✗ no"],
            ["Orientable",          rep.is_orientable ? "✓ yes" : "✗ no"],
            ["Genus (Euler)",       rep.genus == null ? "—" : rep.genus],
          ].map(([k, v]) => (
            <div key={k} className="border border-border/70 px-2 py-1 flex justify-between">
              <span className="text-muted-foreground">{k}</span>
              <span className="text-foreground">{v}</span>
            </div>
          ))}
        </div>
      )}
      {topo && (
        <div className="text-[10px] text-muted-foreground italic border-t border-border pt-2 font-mono">
          Topology snapshot logged — see badge on preview panel.
        </div>
      )}
    </div>
  );
}


/* ---------- shared ---------- */
function FormRow({ label, className = "", children }) {
  return (
    <label className={`space-y-1 ${className}`}>
      <div className="smin-label">{label}</div>
      {children}
    </label>
  );
}
